import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  endOfWeek,
  format,
  getDate,
  isValid,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { useTranslation } from 'react-i18next';

// Longest range we let through. Past a year the day list is unusable and the query gets heavy;
// the reports view is the right tool for that.
export const MAX_RANGE_DAYS = 366;

/**
 * The half of the month a date falls in: the 1st-15th, or the 16th-end of month. Offered as a
 * quick range because a semi-monthly rhythm is common (pay days on the 1st and the 15th), but the
 * UI labels them by their dates only — we don't know any given workshop's pay period.
 * Returns { start, end } Date objects.
 */
export function halfMonthFor(date) {
  if (getDate(date) <= 15) {
    return { start: startOfMonth(date), end: addDays(startOfMonth(date), 14) };
  }
  return { start: addDays(startOfMonth(date), 15), end: endOfMonth(date) };
}

function previousHalfMonthFor(date) {
  const current = halfMonthFor(date);
  // One day before the current half starts always lands inside the previous one.
  return halfMonthFor(addDays(current.start, -1));
}

/**
 * Quick ranges offered above the free date fields. `today` is passed in so callers (and tests)
 * control "now"; all math is local-time (never toISOString, which shifts a Finnish midnight to
 * the previous day).
 */
export function buildRangePresets(today) {
  const currentHalf = halfMonthFor(today);
  const previousHalf = previousHalfMonthFor(today);
  const lastMonth = subMonths(today, 1);

  // Named after which half of the month the range actually is, so the label never contradicts the
  // dates next to it (the "previous" one can be the tail of the previous month).
  const halfLabel = (half) =>
    getDate(half.start) === 1 ? 'worktime.range.presets.firstHalf' : 'worktime.range.presets.secondHalf';

  return [
    {
      key: 'thisWeek',
      labelKey: 'worktime.range.presets.thisWeek',
      start: startOfWeek(today, { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 }),
    },
    {
      key: 'lastWeek',
      labelKey: 'worktime.range.presets.lastWeek',
      start: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      end: endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
    },
    {
      key: 'currentHalfMonth',
      labelKey: halfLabel(currentHalf),
      withDates: true,
      ...currentHalf,
    },
    {
      key: 'previousHalfMonth',
      labelKey: halfLabel(previousHalf),
      withDates: true,
      ...previousHalf,
    },
    {
      key: 'thisMonth',
      labelKey: 'worktime.range.presets.thisMonth',
      start: startOfMonth(today),
      end: endOfMonth(today),
    },
    {
      key: 'lastMonth',
      labelKey: 'worktime.range.presets.lastMonth',
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth),
    },
  ];
}

/**
 * Free date-range chooser for the work time page. Rendered inside a Popover.
 *
 * Preset chips apply immediately (one click is the whole point of them); editing the
 * From/To fields by hand requires the explicit Show button — no save-on-blur.
 *
 * @param {Date} start - currently shown range start
 * @param {Date} end - currently shown range end
 * @param {Function} onApply - called with (startDate, endDate) as Date objects
 * @param {Function} onCancel - close without applying
 * @param {Object} locale - date-fns locale for the calendar
 */
function WorkTimeRangePicker({ start, end, onApply, onCancel, locale }) {
  const { t } = useTranslation();
  const [draftStart, setDraftStart] = useState(start);
  const [draftEnd, setDraftEnd] = useState(end);

  // Reopening the popover should show the range that is actually on screen.
  useEffect(() => {
    setDraftStart(start);
    setDraftEnd(end);
  }, [start, end]);

  const presets = useMemo(() => buildRangePresets(new Date()), []);

  const validationKey = useMemo(() => {
    if (!draftStart || !draftEnd || !isValid(draftStart) || !isValid(draftEnd)) {
      return 'worktime.range.invalidRange';
    }
    if (differenceInCalendarDays(draftEnd, draftStart) < 0) {
      return 'worktime.range.endBeforeStart';
    }
    if (differenceInCalendarDays(draftEnd, draftStart) + 1 > MAX_RANGE_DAYS) {
      return 'worktime.range.tooLong';
    }
    return null;
  }, [draftStart, draftEnd]);

  return (
    <Box sx={{ p: 2, width: { xs: '100%', sm: 380 }, maxWidth: '100vw' }} data-testid="worktime-range-picker">
      <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1.5 }}>
        {t('worktime.range.title')}
      </Typography>

      {/* Quick presets — apply on click */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
        {presets.map((preset) => (
          <Chip
            key={preset.key}
            data-testid={`worktime-range-preset-${preset.key}`}
            label={
              preset.withDates
                ? `${t(preset.labelKey)} (${format(preset.start, 'd.M.')}–${format(preset.end, 'd.M.')})`
                : t(preset.labelKey)
            }
            onClick={() => onApply(preset.start, preset.end)}
            variant="outlined"
            size="small"
            sx={{ cursor: 'pointer' }}
          />
        ))}
      </Box>

      {/* Free range */}
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
          <DatePicker
            label={t('worktime.range.from')}
            value={draftStart}
            onChange={(newValue) => setDraftStart(newValue)}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                inputProps: { 'data-testid': 'worktime-range-from' },
              },
            }}
          />
          <DatePicker
            label={t('worktime.range.to')}
            value={draftEnd}
            onChange={(newValue) => setDraftEnd(newValue)}
            format="dd.MM.yyyy"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                inputProps: { 'data-testid': 'worktime-range-to' },
              },
            }}
          />
        </Box>
      </LocalizationProvider>

      {validationKey && (
        <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 1 }}>
          {t(validationKey, { days: MAX_RANGE_DAYS })}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={onCancel} size="small">
          {t('Cancel')}
        </Button>
        <Button
          onClick={() => onApply(draftStart, draftEnd)}
          size="small"
          variant="contained"
          disabled={Boolean(validationKey)}
          data-testid="worktime-range-apply"
        >
          {t('worktime.range.apply')}
        </Button>
      </Box>
    </Box>
  );
}

export default WorkTimeRangePicker;
